/*
 * Collection / search infinite scroll (Antioch).
 *
 * Rules:
 * - Server-rendered page N stays initially (no full-grid flash)
 * - Scroll down → append N+1…
 * - Scroll up from mid-collection → prepend earlier pages (scroll anchored)
 * - Tekil: earlier page wins. Mid-land seeds prior family keys, then dedupes N.
 * - Prepend replaces a later duplicate with the earlier card.
 * - ?page= tracks the product nearest the viewport center
 * - Pagination stays hidden while infinite scroll is active (CSS :has + class)
 */
if (!customElements.get('product-grid-infinite-scroll')) {
  customElements.define(
    'product-grid-infinite-scroll',
    class ProductGridInfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.loadingPrev = false;
        this.bottomObserver = null;
        this.topObserver = null;
        this.urlObserver = null;
        this.seenFamilies = new Set();
        this.bootstrapped = false;
        this.onBottom = this.onBottom.bind(this);
        this.onTop = this.onTop.bind(this);
        this.onUrlIntersect = this.onUrlIntersect.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);
      }

      connectedCallback() {
        this.grid = this.resolveGrid();
        this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
        this.sectionId = this.dataset.sectionId || (this.grid && this.grid.dataset.id);
        this.spinner = this.querySelector('.infinite-scroll__spinner');
        this.pagination = this.findPagination();
        this.tekil = Boolean(
          window.NodusCollectionProductMode &&
            window.NodusCollectionProductMode.isTekil(this.grid)
        );

        if (!this.grid || !this.sectionId) return;

        if (this.pagination) this.pagination.classList.add('pagination-wrapper--hidden');

        const urlPage = this.pageFromUrl();
        const serverPage = parseInt(this.dataset.currentPage, 10) || 1;
        this.startPage = Math.max(urlPage, serverPage, 1);
        this.endPage = this.startPage;
        this.currentPage = this.endPage;

        this.markRenderedPage(this.startPage);
        this.ensureTopSentinel();
        this.lastScrollY = window.scrollY;

        window.addEventListener('scroll', this.onWindowScroll, { passive: true });
        this.syncUrl(this.endPage);
        this.observeBottom();
        this.observeTop();
        this.observeUrlPages();

        this.boot()
          .catch((error) => console.warn('[infinite-scroll] boot', error))
          .finally(() => {
            this.bootstrapped = true;
            this.kickBottom();
            // Mid-land: top sentinel is often already visible at scrollY≈0.
            if (this.startPage > 1) this.loadPreviousPage();
          });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onWindowScroll);
        this.stop();
      }

      async boot() {
        if (this.tekil) {
          // Drop current-page cards whose family already appeared on earlier pages.
          await this.dropLaterDuplicates();
          this.seenFamilies = window.NodusCollectionProductMode.dedupe(
            this.grid,
            new Set()
          );
        }

        if (window.NodusCollectionProductMode?.backfill) {
          await window.NodusCollectionProductMode.backfill(this.grid);
        }
      }

      /** Mid-land: remove page-N cards that are color siblings of pages 1..N-1. */
      async dropLaterDuplicates() {
        if (!this.tekil || this.startPage <= 1) return;

        const prior = new Set();
        const pages = [];
        for (let page = 1; page < this.startPage; page += 1) pages.push(page);

        const concurrency = 3;
        for (let i = 0; i < pages.length; i += concurrency) {
          const chunk = pages.slice(i, i + concurrency);
          const results = await Promise.all(
            chunk.map((page) => this.fetchPageItemsRaw(page).catch(() => []))
          );
          results.forEach((items) => {
            items.forEach((el) => {
              const key = el.dataset?.familyKey;
              if (key) prior.add(key);
            });
          });
        }

        Array.from(this.grid.children).forEach((el) => {
          if (!this.isProductCard(el)) return;
          const key = el.dataset.familyKey;
          if (key && prior.has(key)) {
            this.urlObserver?.unobserve(el);
            el.remove();
          }
        });
      }

      isNearBottom() {
        const rect = this.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        return rect.top < vh + 900;
      }

      kickBottom() {
        if (this.endPage >= this.totalPages) {
          this.bottomObserver?.disconnect();
          return;
        }
        if (!this.bottomObserver) this.observeBottom();
        else {
          this.bottomObserver.unobserve(this);
          this.bottomObserver.observe(this);
        }
        if (this.isNearBottom()) this.loadNextPage();
      }

      onWindowScroll() {
        const y = window.scrollY;
        if (y > this.lastScrollY + 4 && this.isNearBottom()) {
          this.loadNextPage();
        }
        // At document top, keep trying to backfill earlier pages.
        if (y <= 2 && this.startPage > 1 && !this.loadingPrev) {
          this.loadPreviousPage();
        }
        this.lastScrollY = y;
      }

      resolveGrid() {
        let grid = document.getElementById('product-grid');
        if (!grid) return null;
        if (grid.matches('ul')) return grid;
        return grid.querySelector('ul.product-grid, ul[role="list"]') || grid;
      }

      pageFromUrl() {
        return parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10) || 1;
      }

      syncUrl(page) {
        try {
          const url = new URL(window.location.href);
          if (page <= 1) url.searchParams.delete('page');
          else url.searchParams.set('page', String(page));
          url.searchParams.delete('section_id');
          const next = `${url.pathname}${url.search}${url.hash}`;
          const cur = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          if (next !== cur) history.replaceState({ infiniteScrollPage: page }, '', next);
        } catch (error) {
          console.warn('[infinite-scroll] syncUrl', error);
        }
      }

      markRenderedPage(page) {
        Array.from(this.grid.children).forEach((el) => {
          if (this.isProductCard(el) && !el.dataset.infinitePage) {
            el.dataset.infinitePage = String(page);
          }
        });
      }

      isProductCard(el) {
        if (window.NodusCollectionProductMode?.isProductCard) {
          return window.NodusCollectionProductMode.isProductCard(el);
        }
        if (!el?.classList?.contains('grid__item')) return false;
        if (el.classList.contains('grid__item--banner')) return false;
        if (el.classList.contains('grid__item--banner-cell')) return false;
        return true;
      }

      stop() {
        this.bottomObserver?.disconnect();
        this.topObserver?.disconnect();
        this.urlObserver?.disconnect();
        this.bottomObserver = null;
        this.topObserver = null;
        this.urlObserver = null;
      }

      findPagination() {
        const container = document.getElementById('ProductGridContainer');
        return container ? container.querySelector('.pagination-wrapper') : null;
      }

      ensureTopSentinel() {
        if (this.topSentinel) return;
        this.topSentinel = document.createElement('div');
        this.topSentinel.className = 'infinite-scroll__top-sentinel';
        this.topSentinel.setAttribute('aria-hidden', 'true');
        this.topSentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;';
        this.grid.parentElement?.insertBefore(this.topSentinel, this.grid);
      }

      observeBottom() {
        if (this.endPage >= this.totalPages) return;
        this.bottomObserver = new IntersectionObserver(this.onBottom, {
          rootMargin: '800px 0px',
        });
        this.bottomObserver.observe(this);
      }

      observeTop() {
        if (!this.topSentinel || this.startPage <= 1) return;
        this.topObserver = new IntersectionObserver(this.onTop, {
          rootMargin: '600px 0px 0px 0px',
        });
        this.topObserver.observe(this.topSentinel);
      }

      observeUrlPages() {
        if (!('IntersectionObserver' in window)) return;
        this.urlObserver = new IntersectionObserver(this.onUrlIntersect, {
          root: null,
          rootMargin: '-40% 0px -40% 0px',
          threshold: 0,
        });
        Array.from(this.grid.children).forEach((el) => {
          if (this.isProductCard(el)) this.urlObserver.observe(el);
        });
      }

      watchItems(items) {
        if (!this.urlObserver) return;
        items.forEach((el) => {
          if (this.isProductCard(el)) this.urlObserver.observe(el);
        });
      }

      onUrlIntersect(entries) {
        const hit = entries.find((entry) => entry.isIntersecting);
        if (!hit) return;
        const page = parseInt(hit.target.dataset.infinitePage || '', 10);
        if (Number.isFinite(page) && page > 0) this.syncUrl(page);
      }

      onBottom(entries) {
        if (this.loading || this.loadingPrev || !this.bootstrapped) return;
        if (this.endPage >= this.totalPages) return;
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      }

      onTop(entries) {
        if (this.loading || this.loadingPrev || !this.bootstrapped) return;
        if (this.startPage <= 1) return;
        if (entries.some((entry) => entry.isIntersecting)) this.loadPreviousPage();
      }

      buildPageUrl(page) {
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        params.delete('section_id');
        params.set('section_id', this.sectionId);
        if (page > 1) params.set('page', String(page));
        return `${window.location.pathname}?${params.toString()}`;
      }

      prepareItems(list, page) {
        const items = Array.from(list.children).map((node) =>
          document.importNode(node, true)
        );
        items.forEach((item) => {
          if (this.isProductCard(item)) item.dataset.infinitePage = String(page);
          if (item.classList?.contains('scroll-trigger')) {
            item.classList.add('scroll-trigger--cancel');
          }
          item.querySelectorAll?.('.scroll-trigger').forEach((el) => {
            el.classList.add('scroll-trigger--cancel');
          });
        });
        return items;
      }

      async fetchPageItemsRaw(page) {
        const response = await fetch(this.buildPageUrl(page));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = doc.getElementById('product-grid');
        if (!root) throw new Error('product-grid missing');
        const list = root.matches('ul')
          ? root
          : root.querySelector('ul.product-grid, ul[role="list"]') || root;
        return this.prepareItems(list, page);
      }

      /** Append direction: skip families already shown. */
      applyTekilAppend(items) {
        if (!this.tekil || !window.NodusCollectionProductMode) return items;
        return window.NodusCollectionProductMode.filterIncoming(
          items,
          this.seenFamilies,
          true
        );
      }

      /**
       * Prepend direction: earlier page wins.
       * If a later duplicate is already in the grid, replace it with the earlier card.
       */
      applyTekilPrepend(items) {
        if (!this.tekil) return items;
        const kept = [];
        const pageSeen = new Set();
        items.forEach((el) => {
          if (!this.isProductCard(el)) {
            kept.push(el);
            return;
          }
          const key = el.dataset.familyKey;
          if (!key) {
            kept.push(el);
            return;
          }
          if (pageSeen.has(key)) return;
          pageSeen.add(key);

          const existing = this.grid.querySelector(
            `[data-family-key="${CSS.escape(key)}"]`
          );
          if (existing) {
            this.urlObserver?.unobserve(existing);
            existing.remove();
            this.seenFamilies.add(key);
            kept.push(el);
            return;
          }

          this.seenFamilies.add(key);
          kept.push(el);
        });
        return kept;
      }

      async loadPreviousPage() {
        if (this.loading || this.loadingPrev || this.startPage <= 1) return;

        this.loadingPrev = true;
        try {
          let guard = 0;
          while (this.startPage > 1 && guard < 12) {
            guard += 1;
            const prevPage = this.startPage - 1;
            const raw = await this.fetchPageItemsRaw(prevPage);
            const items = this.applyTekilPrepend(raw);
            this.startPage = prevPage;

            if (!items.length) {
              if (this.startPage <= 1) this.topObserver?.disconnect();
              continue;
            }

            const anchor = this.grid.firstElementChild;
            const beforeTop = anchor ? anchor.getBoundingClientRect().top : 0;

            const fragment = document.createDocumentFragment();
            items.forEach((item) => fragment.appendChild(item));
            this.grid.prepend(fragment);
            this.watchItems(items);

            if (anchor) {
              const afterTop = anchor.getBoundingClientRect().top;
              window.scrollBy(0, afterTop - beforeTop);
            }
            break;
          }
          if (this.startPage <= 1) this.topObserver?.disconnect();
        } catch (error) {
          console.warn('[infinite-scroll] previous', error);
        } finally {
          this.loadingPrev = false;
        }
      }

      async loadNextPage(options = {}) {
        if (this.loading || this.loadingPrev || !this.bootstrapped) return;
        if (this.endPage >= this.totalPages) return;

        this.loading = true;
        const silent = Boolean(options.silent);
        if (this.spinner && !silent) this.spinner.hidden = false;

        try {
          // Drain tekil-empty pages so the last unique products always appear.
          let guard = 0;
          while (this.endPage < this.totalPages && guard < 24) {
            guard += 1;
            const nextPage = this.endPage + 1;
            const raw = await this.fetchPageItemsRaw(nextPage);
            const items = this.applyTekilAppend(raw);

            this.endPage = nextPage;
            this.currentPage = this.endPage;
            this.dataset.currentPage = String(this.endPage);

            if (!items.length) continue;

            items.forEach((item) => this.grid.appendChild(item));
            this.watchItems(items);
            if (!silent) this.syncUrl(this.endPage);
            break;
          }

          if (this.endPage >= this.totalPages) {
            this.bottomObserver?.disconnect();
          } else {
            this.kickBottom();
          }
        } catch (error) {
          if (this.pagination) this.pagination.classList.remove('pagination-wrapper--hidden');
          this.bottomObserver?.disconnect();
          console.warn('[infinite-scroll]', error);
        } finally {
          this.loading = false;
          if (this.spinner) this.spinner.hidden = true;
          if (this.endPage < this.totalPages && this.isNearBottom()) {
            queueMicrotask(() => this.loadNextPage());
          }
        }
      }
    }
  );
}
